import i18next from 'i18next';
import { Instance, SnapshotOut, types } from 'mobx-state-tree';
import { setAnalyticsUser, trackEvent } from 'src/analytics';
import { CTBT_MSG_READ_KEY, LANGUAGE } from 'src/assets/static/constants';
import { sendWidgetData } from 'src/hooks/useWebViewCommunication';
import { ILoginData, IRegister } from 'src/screens';
import { IPasswordChangeRequest, IPasswordResetRequest } from 'src/screens/auth/types';
import { authApi } from 'src/services';

import { withRootStore } from '../extensions/withRootStore';
import { withSetPropAction } from '../extensions/withSetPropAction';
import { User } from '../user/User';

/**
 * # AuthStore
 */
export const AuthStore = types
  .model('AuthStore')
  .props({
    isAuthenticated: types.optional(types.boolean, false),
    isInitialized: types.optional(types.boolean, false),
    user: types.optional(types.maybeNull(User), null),
    isSaved: types.optional(types.boolean, false),
  })
  .extend(withRootStore)
  .actions(withSetPropAction)
  .actions((_self) => ({
    getAccessToken() {
      return localStorage.getItem('accessToken');
    },
    setAccessToken(token: string) {
      localStorage.setItem('accessToken', token);
    },
    getRefreshToken() {
      return localStorage.getItem('refreshToken');
    },
    setRefreshToken(token: string) {
      localStorage.setItem('refreshToken', token);
    },
  }))
  .actions((_self) => ({
    initAuth() {
      _self.setProp('isAuthenticated', false);
      // 토큰을 완전히 삭제 (빈 문자열이 아닌 키 자체 제거)
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // 케어 코치 '새 메시지' 확인 상태도 초기화 (사용자 간 상태 누수 방지)
      localStorage.removeItem(CTBT_MSG_READ_KEY);
      // 언어 설정은 유지 (로그아웃 후에도 선택한 언어로 표시)
    },
  }))
  .actions((self) => {
    /** 내 정보 조회 */
    const getUserProfile = async () => {
      try {
        const result = await authApi.getUserProfile();
        if (result?.resultCode === 'S' && result.data) {
          const userProfile = result.data;
          if (userProfile) {
            self.setProp('user', userProfile);
            self.setProp('isAuthenticated', true);
            i18next.changeLanguage(userProfile.langCd || LANGUAGE.korean);
            setAnalyticsUser({
              user_id: String(userProfile.userSid),
              language: userProfile.langCd ?? undefined,
              gender: userProfile.genderTypeCd?.value ?? undefined,
              hospital_name: userProfile.hsptlInfo?.hsptlNm ?? undefined,
              // GLP-1 도메인 필드는 BE 합의 후 추가:
              //   drug_code: userProfile.drugCode,
              //   start_date: userProfile.startDate,
              //   treatment_weeks: ...,
            });
          }
        } else {
          self.initAuth();
        }
        self.setProp('isInitialized', true);
        return result;
      } catch (error) {
        self.initAuth();
      }
    };

    return { getUserProfile };
  })
  .actions((self) => {
    /** 로그인 */
    const login = async (loginData: ILoginData) => {
      try {
        const result = await authApi.login(loginData);
        if (result?.resultCode === 'S' && result.data) {
          const loginResponse = result.data;
          if (loginResponse) {
            // 토큰 저장 — access/refresh 모두 localStorage 보관
            self.setAccessToken(loginResponse.accessToken);
            if (loginResponse.refreshToken) {
              self.setRefreshToken(loginResponse.refreshToken);
            }
            // 신규 로그인 — 케어 코치 '새 메시지' 확인 상태 초기화(배지 재노출)
            localStorage.removeItem(CTBT_MSG_READ_KEY);

            // 로그인 성공 후 바로 프로필 조회하여 사용자 정보 세팅
            await self.getUserProfile();
          }
          trackEvent('auth_login', { method: 'password', success: true });
        } else {
          self.initAuth();
          trackEvent('auth_login', {
            method: 'password',
            success: false,
            error_code: result?.resultCode ?? 'unknown',
          });
        }
        return result;
      } catch (error) {
        self.initAuth();
        trackEvent('auth_login', { method: 'password', success: false, error_code: 'exception' });
      }
    };

    /** SNS 로그인 (백엔드 OAuth 콜백에서 받은 토큰 데이터로 로그인) */
    const loginSns = async (data: {
      accessToken: string;
      refreshToken?: string;
      tokenType: string;
      expiresIn: number;
      user: {
        userSid: number;
        loginId: string;
        userNm: string | null;
        acntSttsCdValue: number;
      };
    }) => {
      try {
        // 토큰 저장 — access/refresh 모두 localStorage 보관
        self.setAccessToken(data.accessToken);
        if (data.refreshToken) {
          self.setRefreshToken(data.refreshToken);
        }
        // 신규 로그인 — 케어 코치 '새 메시지' 확인 상태 초기화(배지 재노출)
        localStorage.removeItem(CTBT_MSG_READ_KEY);

        // SNS 로그인 성공 후 프로필 조회하여 사용자 정보 세팅 (일반 로그인과 동일한 흐름)
        await self.getUserProfile();

        return { success: true };
      } catch (error) {
        console.error('[AuthStore] loginSns error:', error);
        self.initAuth();
        return {
          success: false,
          message: error instanceof Error ? error.message : 'SNS 로그인 실패',
        };
      }
    };

    /** 로그아웃 */
    const logout = async () => {
      // 클라이언트 측 상태를 먼저 정리 (API 호출 실패해도 로그아웃 보장)
      const cleanupClient = () => {
        self.initAuth();
        self.setProp('user', null);
        trackEvent('auth_logout');
        setAnalyticsUser(null);
        sendWidgetData({
          isLoggedIn: false,
        });
      };

      try {
        // 백엔드 로그아웃 API 호출 (HTTP-only cookie 무효화)
        await authApi.logout();
      } catch (error) {
        console.warn('[AuthStore] 로그아웃 API 호출 실패 (클라이언트는 이미 정리됨):', error);
      } finally {
        // API 성공/실패 여부와 관계없이 클라이언트 측 정리
        cleanupClient();
      }
    };

    /** 아이디 중복 확인 */
    const checkDuplicateLoginId = async (loginId: string) => {
      try {
        const result = await authApi.checkDuplicateLoginId(loginId);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 아이디 찾기 (이메일 기반 — 본인인증 제거) */
    const findLoginId = async (email: string) => {
      try {
        const result = await authApi.findLoginId(email);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 비밀번호 재설정(로그인 이전) */
    const resetPwd = async (data: IPasswordResetRequest) => {
      try {
        const result = await authApi.resetPwd(data);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 토큰 재발급 — refresh token은 localStorage에서 꺼내 바디로 전송 */
    const loginRefresh = async () => {
      try {
        const refreshToken = self.getRefreshToken();
        const result = await authApi.loginRefresh(refreshToken);
        if (result?.resultCode === 'S' && result.data) {
          const refreshResponse = result.data;
          if (refreshResponse) {
            // 새로운 액세스 토큰 저장
            self.setAccessToken(refreshResponse.accessToken);
            // refresh token이 갱신되어 내려오면 함께 저장
            if (refreshResponse.refreshTokenRenewed && refreshResponse.refreshToken) {
              self.setRefreshToken(refreshResponse.refreshToken);
            }
          }
        }
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 비밀번호 변경(로그인 이후) */
    const changePwd = async (data: IPasswordChangeRequest) => {
      try {
        const result = await authApi.changePwd(data);
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    /** 회원가입 */
    const signup = async (formData: IRegister) => {
      try {
        const result = await authApi.signup(formData as unknown as authApi.ISignupRequest);
        if (result?.resultCode === 'S' && result.data) {
          const signupResponse = result.data;
          if (signupResponse) {
            self.setAccessToken(signupResponse.accessToken);
            // Refresh token은 HTTP-only cookie로 관리

            // 회원가입 성공 후 바로 프로필 조회하여 사용자 정보 세팅
            await self.getUserProfile();
          }
          trackEvent('auth_signup_complete');
        } else {
          self.initAuth();
        }
        return result;
      } catch (error) {
        self.initAuth();
      }
    };

    /** 언어 변경 */
    const changeLanguage = async (langCd: string) => {
      try {
        const result = await authApi.changeLanguage(langCd, self.getRefreshToken());
        if (result?.resultCode === 'S' && result.data) {
          const oauthToken = result.data;
          if (oauthToken) {
            self.setAccessToken(oauthToken?.accessToken);
            if (oauthToken?.refreshToken) {
              self.setRefreshToken(oauthToken.refreshToken);
            }
          }
        } else {
          self.initAuth();
        }
        return result;
      } catch (error) {
        self.initAuth();
      }
    };

    /** 회원탈퇴 */
    const withdrawal = async () => {
      try {
        const result = await authApi.withdrawal();
        if (result?.resultCode === 'S') {
          self.initAuth();
          self.setProp('user', null);
          // 언어 설정은 유지 (탈퇴 후에도 선택한 언어로 표시)
        }
        return result;
      } catch (error) {
        // Error handling by Axios interceptor
      }
    };

    return {
      login,
      loginSns,
      logout,
      checkDuplicateLoginId,
      findLoginId,
      resetPwd,
      loginRefresh,
      changePwd,
      signup,
      changeLanguage,
      withdrawal,
    };
  });

type TAuthStore = Instance<typeof AuthStore>;
type TAuthStoreSnapshot = SnapshotOut<typeof AuthStore>;
export interface IAuthStore extends TAuthStore {}
export type TAuthStoreKeys = keyof TAuthStoreSnapshot & string;
export interface IAuthStoreSnapshot extends TAuthStoreSnapshot {}
